import type { SyncEngine } from '../sync-engine.ts'

/** Syncs when the connection comes back or the tab becomes visible. Returns a detach function. */
export function attachBrowserTriggers(engine: Pick<SyncEngine, 'trigger'>): () => void {
  const onOnline = () => engine.trigger()
  const onVisible = () => {
    if (document.visibilityState === 'visible') engine.trigger()
  }
  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onVisible)
  return () => {
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisible)
  }
}

export interface StorageStatus {
  /** The browser promised not to evict our data under storage pressure. */
  persisted: boolean
  usageBytes: number | null
  quotaBytes: number | null
}

/**
 * Asks the browser to keep our data (call once after sign-in). When this is
 * false (common on iOS Safari), show a banner: unsynced bills could be evicted.
 */
export async function requestPersistentStorage(): Promise<StorageStatus> {
  const storage = navigator.storage
  let persisted = false
  if (storage?.persisted) persisted = await storage.persisted()
  if (!persisted && storage?.persist) persisted = await storage.persist().catch(() => false)
  const estimate = storage?.estimate ? await storage.estimate().catch(() => null) : null
  return { persisted, usageBytes: estimate?.usage ?? null, quotaBytes: estimate?.quota ?? null }
}
